import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useToast } from "@/components/ui/use-toast";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";

import { ErrorType } from "@/types";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react"
import { SystemSettings } from "@/types/system-setting/system-config";
import { useTestEmailMutation, useUpdateSystemSettingsMutation } from "@/services/systemConfigApi";

interface EmailSettingsFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: string;
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: any;
  editModeData: any | undefined;
}
const EmailSettingsForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  editModeData,
  refetch,
}: EmailSettingsFormProps) => {

  const [showPassword, setShowPassword] = useState(false)


  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const userInfo = useSelector((state: RootState) => state.auth.userInfo?.user);
  const { toast } = useToast();
  const [updateSetting, { isLoading, error, status }] = useUpdateSystemSettingsMutation();
  const [testEmail, { isLoading: testEmailLoading, error: testEmailError, status: testEmailStatus }] = useTestEmailMutation();

  const initialValues = {
    group_name: "smtp_config",
    sender_email: "",
    smtp_host: "",
    smtp_port: "",
    smtp_username: "",
    smtp_password: "",
    cc_email: "",
    bcc_email: "",
  };

  const form = useForm<SystemSettings>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  useEffect(() => {
    if (action == "edit") {
      const payload = { ...editModeData, group_name: "smtp_config" };
      reset(payload as SystemSettings);
    } else {
      reset(initialValues);
    }
  }, [action]);

  const handleTestEmail = async () => {
    const payload = {
      name: "",
      subject: "",
      body: ""
    }
    const resp = await testEmail(payload).unwrap();
    if (resp) {
      toast({
        variant: "success",
        title: "Email sent successfully",
      });
    }
  };

  const onSubmit = async (formData: SystemSettings) => {
    const { group_name, ...payload } = formData;
    try {
      const resp = await updateSetting({ smtp_config: payload }).unwrap();
      if (!error && (status === "fulfilled" || status == 'uninitialized')) {
        toast({
          variant: "success",
          title: "Invoice updated Successfully",
        });
        refetch();
        handleClose();
        return resp;
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
        return false;
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
        return false;
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setAction("add");
    reset(
      initialValues,
      {
        keepIsSubmitted: false, // Reset the form's submission state
        keepSubmitCount: false, // Reset the count of submissions
        keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
        keepDefaultValues: true, // Keep default values as provided by initialValue
      }
    );

    setOpen(false);
  };

  console.log({ watcher, errors, action });
  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1100px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-6 sticky top-0 z-40 py-3 bg-white">
              <SheetTitle>
                <div className="flex justify-between flex-col sm:flex-row gap-3 sm:gap-5 items-start bg-white">
                  <div>
                    <p className="font-semibold">System Configuration</p>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">
                        System Configuration
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold ">
                        {action == "add" ? "Create" : "Edit"} Email Settings
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-end sm:justify-center space-x-3 sm:space-x-[20px] w-full sm:w-auto">
                    <Button
                      type="button"
                      className="w-[100px] text-center flex items-center gap-2 border-primary"
                      variant={"outline"}
                      disabled={isSubmitting}
                      onClick={handleClose}
                    >
                      <i className="fa fa-xmark "></i>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      className="w-fit text-center flex items-center gap-2 border-primary"
                      variant={"outline"}
                      disabled={isSubmitting || testEmailLoading}
                      onClick={handleTestEmail}
                    >
                      <i className="fa-regular fa-envelope "></i>
                      Test Email
                    </Button>
                    <LoadingButton
                      type="submit"
                      className="w-[110px] cursor-pointer bg-primary hover:bg-primary flex items-center gap-1"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {!isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                      )}
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <Separator className=" h-[1px] rounded-full     " />

            <SheetDescription className=" h-full  p-6  ">
              <Card className="w-full ">
                <CardHeader>
                  <CardTitle className="text-base font-medium">
                    Email Settings
                  </CardTitle>
                  <Separator className="h-[1px]" />
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6">
                    {/* First Row */}
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="space-y-2">
                        <Label
                          htmlFor="senderEmail"
                          className="text-sm text-zinc-500"
                        >
                          Sender Email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="senderEmail"
                          placeholder="Enter Gym Email"
                          type="email"
                          value={watcher.sender_email}
                          className="h-10 placeholder:text-zinc-400"
                          {...register("sender_email", {
                            required: "Required",
                            pattern: {
                              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                              message: "Invalid email address",
                            },
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="smtpHost"
                          className="text-sm text-zinc-500"
                        >
                          SMTP Host <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="smtpHost"
                          placeholder="Enter SMTP Host"
                          value={watcher.smtp_host}
                          className="h-10 placeholder:text-zinc-400"
                          {...register("smtp_host", {
                            required: "Required",
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="smtpPassword"
                          className="text-sm text-zinc-500"
                        >
                          SMTP Password <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="smtpPassword"
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter SMTP Password"
                            value={watcher.smtp_password}
                            className="h-10 pr-10 placeholder:text-zinc-400"
                            {...register("smtp_password", {
                              required: "Required",
                            })}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-2.5 text-zinc-400 hover:text-zinc-600"
                          >
                            {showPassword ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Second Row */}
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="space-y-2">
                        <Label
                          htmlFor="smtpPort"
                          className="text-sm text-zinc-500"
                        >
                          SMTP Port <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="smtpPort"
                          placeholder="Enter SMTP Port"
                          type="number"
                          value={watcher.smtp_port}
                          className="h-10 placeholder:text-zinc-400"
                          {...register("smtp_port", {
                            required: "Required",
                            pattern: {
                              value: /^[0-9]+$/,
                              message: "Invalid port number",
                            },
                            setValueAs: (value) => value.toString()
                          })}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="smtpUsername"
                          className="text-sm text-zinc-500"
                        >
                          SMTP Username <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="smtpUsername"
                          type="text"
                          value={watcher.smtp_username}
                          placeholder="Enter SMTP Username"
                          className="h-10 placeholder:text-zinc-400"
                          {...register("smtp_username", {
                            required: "Required",
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cc" className="text-sm text-zinc-500">
                          CC
                        </Label>
                        <Input
                          id="cc"
                          placeholder="Enter CC"
                          type="email"
                          value={watcher.cc_email}
                          className="h-10 placeholder:text-zinc-400"
                          {...register("cc_email", {
                            pattern: {
                              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                              message: "Invalid email address",
                            },
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bcc" className="text-sm text-zinc-500">
                          BCC
                        </Label>
                        <Input
                          id="bcc"
                          placeholder="Enter Bcc"
                          type="email"
                          value={watcher.bcc_email}
                          className="h-10 placeholder:text-zinc-400"
                          {...register("bcc_email", {
                            pattern: {
                              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                              message: "Invalid email address",
                            },
                          })}
                        />
                      </div>
                    </div>


                  </div>
                </CardContent>
              </Card>
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default EmailSettingsForm;
